import React from 'react';
import { CheckCircle, XCircle } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { SkeletonTable } from '../ui/Skeleton';
import { ErrorState } from '../ui/EmptyState';
import { useActionRuns } from '../../hooks/useScheduledActions';
import { formatDate } from '../../utils/dateUtils';
import { useLanguage } from '../../lang/LangContext';
import { KEYS } from '../../lang/keys';


const resultTypeLabel = (resultType) => {
    if (!resultType) return 'â€”';
    // Strip PHP namespace prefix e.g. 'App\Models\Invoice' â†’ 'Invoice'
    const parts = resultType.split('\\');
    return parts[parts.length - 1];
};

const ViewActionRunsModal = ({ isOpen, onClose, action }) => {
    const { data: runs = [], isLoading, error, refetch } = useActionRuns(
        isOpen ? action?.uid : null
    );
    const { t } = useLanguage();

    const typeLabels = {
        create_invoice: t(KEYS.SERVICE_ACTIONS_TYPE_CREATE_INVOICE),
        create_reading: t(KEYS.SERVICE_ACTIONS_TYPE_CREATE_READING),
    };

    const frequencyLabels = {
        once: t(KEYS.SERVICE_ACTIONS_FREQ_ONCE),
        daily: t(KEYS.SERVICE_ACTIONS_FREQ_DAILY),
        weekly: t(KEYS.SERVICE_ACTIONS_FREQ_WEEKLY),
        monthly: t(KEYS.SERVICE_ACTIONS_FREQ_MONTHLY),
        yearly: t(KEYS.SERVICE_ACTIONS_FREQ_YEARLY),
    };

    const typeLabel = typeLabels[action?.type] ?? action?.type ?? 'â€”';
    const frequencyLabel = frequencyLabels[action?.frequency] ?? action?.frequency ?? 'â€”';

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Action Runs"
            subtitle={`${typeLabel} Â· ${frequencyLabel}`}
            size="large"
            footer={
                <Button variant="outline" onClick={onClose}>
                    {t(KEYS.COMMON_CLOSE)}
                </Button>
            }
        >
            {isLoading ? (
                <SkeletonTable rows={4} columns={4} />
            ) : error ? (
                <ErrorState message={error?.message || 'Failed to load runs'} onRetry={refetch} />
            ) : runs.length > 0 ? (
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-surface-border">
                                <th className="px-4 py-2.5 text-left text-[10px] font-medium text-text-muted uppercase tracking-widest">{t(KEYS.ACTION_RUNS_RAN_AT)}</th>
                                <th className="px-4 py-2.5 text-left text-[10px] font-medium text-text-muted uppercase tracking-widest">{t(KEYS.ACTION_RUNS_STATUS)}</th>
                                <th className="px-4 py-2.5 text-left text-[10px] font-medium text-text-muted uppercase tracking-widest hidden sm:table-cell">{t(KEYS.ACTION_RUNS_RESULT)}</th>
                                <th className="px-4 py-2.5 text-left text-[10px] font-medium text-text-muted uppercase tracking-widest">{t(KEYS.ACTION_RUNS_ERROR)}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-surface-border">
                            {runs.map((run, index) => (
                                <tr
                                    key={run.uid}
                                    className="hover:bg-surface-overlay transition-colors animate-appear"
                                    style={{ animationDelay: `${index * 30}ms` }}
                                >
                                    <td className="px-4 py-2.5 text-xs font-mono text-text-muted whitespace-nowrap">
                                        {formatDate(run.ran_at)}
                                    </td>
                                    <td className="px-4 py-2.5">
                                        {run.was_successful ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider bg-status-paid-bg text-status-paid">
                                                <CheckCircle className="w-3 h-3" />
                                                {t(KEYS.ACTION_RUNS_SUCCESS)}
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider bg-status-overdue-bg text-status-overdue">
                                                <XCircle className="w-3 h-3" />
                                                {t(KEYS.ACTION_RUNS_FAILED)}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-2.5 text-sm text-text-muted hidden sm:table-cell">
                                        {run.result_type ? (
                                            <span>{resultTypeLabel(run.result_type)} #{run.result_id}</span>
                                        ) : 'â€”'}
                                    </td>
                                    <td className="px-4 py-2.5 text-xs text-status-overdue max-w-xs truncate">
                                        {run.error_message ?? 'â€”'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center py-10 text-center">
                    <p className="text-sm text-text-secondary">{t(KEYS.ACTION_RUNS_EMPTY)}</p>
                </div>
            )}
        </Modal>
    );
};

export default ViewActionRunsModal;
